// Times played per character, as a row of columns with the character's token sitting on top of
// each bar and its count above that — the way Balatro shows most-played Jokers. Bars are coloured
// by side (good, evil, or neither for a Traveler), not by team: the icon already says exactly
// which role it was, so the colour only needs to answer "which way were they pulling".

import type { RoleCount } from "@cc/shared";
import { characterIcon, percent, sideColorClass } from "../../lib/history.js";
import { cn, teamLabel } from "../../lib/ui.js";
import { Tooltip } from "../ui/primitives.js";

const BAR_MAX_PX = 112;

export function RoleBars({ roles, emptyText }: { roles: RoleCount[]; emptyText: string }) {
  if (roles.length === 0) {
    return <p className="py-8 text-center text-xs text-parchment-dim">{emptyText}</p>;
  }
  const max = Math.max(...roles.map((r) => r.count));

  return (
    <div className="cc-scroll overflow-x-auto pb-1">
      <ol className="m-0 flex list-none items-end gap-2 px-1" style={{ minWidth: "max-content" }}>
        {roles.map((role) => {
          const icon = characterIcon(role.characterId);
          // Never a zero-height bar: even one game deserves a visible stub under its token.
          const height = Math.max(6, Math.round((role.count / max) * BAR_MAX_PX));
          return (
            <li key={role.characterId} className="flex w-[4.75rem] shrink-0 flex-col items-center">
              <Tooltip
                content={
                  <div className="space-y-0.5">
                    <div className="font-medium">{role.characterName}</div>
                    <div className="text-parchment-dim">{teamLabel(role.team)}</div>
                    <div className="text-parchment-dim">
                      {role.count} {role.count === 1 ? "game" : "games"}
                      {role.decided > 0 && ` · won ${percent(role.wins, role.decided)}`}
                    </div>
                  </div>
                }
              >
                <div className="flex cursor-default flex-col items-center">
                  <span className="text-sm font-semibold tabular-nums text-parchment">{role.count}</span>
                  {icon ? (
                    <img src={icon} alt="" className="size-12 object-contain" />
                  ) : (
                    <span className="grid size-12 place-items-center rounded-full border border-ink-line text-[10px] text-parchment-dim">
                      ?
                    </span>
                  )}
                  <span
                    aria-hidden
                    className={cn("mt-1 w-6 rounded-t", sideColorClass(role.team))}
                    style={{ height }}
                  />
                </div>
              </Tooltip>
              <span className="mt-1 h-7 w-full border-t border-ink-line pt-1 text-center text-[10px] leading-tight text-parchment-dim">
                <span className="line-clamp-2 break-normal">{role.characterName}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
